// src/Pages/pro/choisir_profil.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FormProResubmit from "../../Component/FormProResubmit/FormProResubmit";
import API, { BASE_URL } from "../../api"; // ✅ axios (baseURL normalisée, withCredentials)
import "./choisirProfil.css";

export default function ChoisirProfil() {
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const token  = localStorage.getItem("token");

  const [loading, setLoading]   = useState(true);
  const [annonce, setAnnonce]   = useState(null);
  const [user, setUser]         = useState(null);
  const [error, setError]       = useState("");
  const [query, setQuery]       = useState("");

  /* ------------- utils ------------- */
  const normalize = (v) =>
    String(v ?? "")
      .normalize("NFD").replace(/\p{Diacritic}/gu, "")
      .trim().toLowerCase();

  const extractUser = (payload) => {
    if (!payload || typeof payload !== "object") return null;
    if (payload.user && typeof payload.user === "object") return payload.user;
    if (payload.data?.user && typeof payload.data.user === "object") return payload.data.user;
    if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) return payload.data;
    return payload;
  };

  const hasAnnonce = (a) => !!(a && typeof a === "object" && a._id && String(a._id).length > 0);

  /* ------------- loaders ------------- */
  const loadAnnonce = useCallback(async () => {
    const res  = await API.get(`/api/annonces/by-user/${userId}`);
    const data = res?.data ?? null;
    const list = Array.isArray(data) ? data : (data ? [data] : []);
    setAnnonce(list[0] || null);
  }, [userId]);

  const loadUser = useCallback(async () => {
    try {
      const res = await API.get(`/api/users/${userId}`);
      const u   = extractUser(res?.data);
      setUser(u || null);
      return u;
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/auth/login");
        return null;
      }
      setUser(null);
      return null;
    }
  }, [userId, navigate]);

  const loadAll = useCallback(async () => {
    if (!userId) { setError("Utilisateur non connecté."); setLoading(false); return; }
    setLoading(true); setError("");
    try {
      await Promise.all([loadAnnonce(), loadUser()]);
    } catch (e) {
      console.error(e);
      setError("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }, [userId, loadAnnonce, loadUser]);

  useEffect(() => {
    loadAll();
    const onAnnonceUpdated = () => loadAll();
    const onAuthChanged    = () => loadAll();
    const onStorage = (e) => { if (["userId","token"].includes(e.key)) loadAll(); };

    window.addEventListener("annonce:updated", onAnnonceUpdated);
    window.addEventListener("auth:changed", onAuthChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("annonce:updated", onAnnonceUpdated);
      window.removeEventListener("auth:changed", onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [loadAll]);

  /* ------------- états dérivés ------------- */
  const role       = user?.role;
  const proStatus  = user?.proReview?.status; // "approved" | "pending" | "refused"
  const abonnement = (user?.abonnement || "").trim().toLowerCase();

  const profils = useMemo(() => annonce?.profils || [], [annonce]);
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return profils;
    return profils.filter((p) =>
      [p.name, p.profession].filter(Boolean).some((v) => normalize(v).includes(q))
    );
  }, [profils, query]);

  const isRefused =
    ["refuse","refus","rejected","rejete","rejetee"].includes(normalize(annonce?.statut)) ||
    ["refuse","refus","rejected","rejete","rejetee"].includes(normalize(proStatus));

  const showAboBanner = proStatus === "approved" && !abonnement;

  const adminComment =
    annonce?.commentaireAdmin ||
    user?.commentaireAdmin ||
    user?.refusMessage ||
    user?.rejectReason ||
    user?.proReview?.note || "";

  /* ------------- actions ------------- */
  const selectProfil = (profil) => {
    localStorage.setItem("selectedProfilId", profil._id || "");
    localStorage.setItem("selectedProfil", JSON.stringify({
      _id: profil._id || "",
      name: profil.name || "",
      profession: profil.profession || "",
    }));
    window.dispatchEvent(new CustomEvent("profil:selected", { detail: { profilId: profil._id } }));
    navigate("/pro/calendar");
  };

  const ensureAboOrRedirect = () => {
    if (proStatus === "approved" && !abonnement) {
      navigate("/billing/subscription");
      return false;
    }
    return true;
  };

  const goCreateAnnonce = async () => {
    if (!ensureAboOrRedirect()) return;

    try {
      const res  = await API.get(`/api/annonces/by-user/${userId}`);
      const data = res?.data ?? null;
      const list = Array.isArray(data) ? data : (data ? [data] : []);
      const curr = list[0] || null;

      if (curr?._id) return navigate(`/pro/listing?id=${curr._id}`);

      localStorage.removeItem("annonceId");
      sessionStorage.removeItem("annonceId");
      sessionStorage.setItem("forceCreateAnnonce", "true");
      navigate("/pro/listing/edit?mode=create");
    } catch {
      localStorage.removeItem("annonceId");
      sessionStorage.setItem("forceCreateAnnonce", "true");
      navigate("/pro/listing/edit?mode=create");
    }
  };

  const goManageAnnonce = async () => {
    if (!ensureAboOrRedirect()) return;

    try {
      const res  = await API.get(`/api/annonces/by-user/${userId}`);
      const data = res?.data ?? null;
      const list = Array.isArray(data) ? data : (data ? [data] : []);
      const curr = list[0] || null;

      if (curr?._id) {
        navigate(`/editAnnonce?id=${curr._id}`);
      } else {
        localStorage.removeItem("annonceId");
        sessionStorage.setItem("forceCreateAnnonce","true");
        navigate("/pro/listing/edit?mode=create");
      }
    } catch {
      localStorage.removeItem("annonceId");
      sessionStorage.setItem("forceCreateAnnonce","true");
      navigate("/pro/listing/edit?mode=create");
    }
  };

  /* ------------- render ------------- */
  if (loading) return <div className="choisir-wrap"><div className="choisir-card"><div className="muted">Chargement…</div></div></div>;
  if (error)   return <div className="choisir-wrap"><div className="choisir-card"><div className="alert alert--error">{error}</div></div></div>;
  if (role !== "pro") {
    return (
      <div className="choisir-wrap">
        <div className="choisir-card">
          <h2 className="choisir-title">Accès réservé</h2>
          <p className="muted">Cette page est réservée aux comptes professionnels.</p>
          <button className="btn btn-ghost" onClick={() => navigate("/")}>Retour</button>
        </div>
      </div>
    );
  }

  // Si refusé : on laisse l'écran avec formulaire de re-soumission
  // Si pending : tu peux afficher un bandeau, mais on ne force pas la redirection
  return (
    <div className="choisir-wrap">
      <div className="choisir-card">
        <h2 className="choisir-title">Choisir un profil</h2>

        {/* === PENDING === */}
        {proStatus === "pending" && (
          <div className="alert alert--info" style={{ marginBottom: 12 }}>
            Votre dossier est en cours de vérification. Vous pourrez choisir une formule dès validation.
          </div>
        )}

        {/* === APPROVED SANS ABO : info + CTA (pas de redirect auto) === */}
        {showAboBanner && (
          <div className="alert alert--warning" style={{ marginBottom: 12 }}>
            Votre compte est validé — choisissez un abonnement pour créer votre annonce.
            <button className="btn btn-primary" style={{ marginLeft: 8 }} onClick={() => navigate("/billing/subscription")}>
              Choisir un abonnement
            </button>
          </div>
        )}

        {/* === SI REFUSÉ : bannière + Formulaire de re-soumission === */}
        {isRefused && (
          <>
            <div className="refus-banner">
              <div className="refus-title">Votre compte professionnel a été refusé.</div>
              {adminComment ? (
                <p className="refus-comment"><span>Motif :</span> {adminComment}</p>
              ) : null}
              <p className="refus-sub">
                Merci de corriger vos informations et de renvoyer votre dossier.
              </p>
            </div>

            <FormProResubmit
              apiBase={BASE_URL}          // ⚠️ string propre (pas l’instance axios)
              userId={userId}
              annonceId={annonce?._id}
              token={token}
              initialValues={{
                nameCompany:  annonce?.nameCompany  || user?.nameCompany  || "",
                phoneNumber:  annonce?.phoneNumber  || user?.phoneNumber  || "",
                adresCompany: annonce?.adresCompany || user?.adresCompany || "",
                city:         annonce?.city         || user?.city         || "",
                postalCode:   annonce?.postalCode   || user?.postalCode   || "",
                siret:        annonce?.siret        || user?.siret        || "",
                pitch:        annonce?.description  || "",
              }}
              onSuccess={() => {
                setAnnonce((a) => a ? { ...a, statut: "en_attente", commentaireAdmin: "" } : a);
                loadAll();
              }}
            />

            <hr style={{ margin:"18px 0", border:"none", borderTop:"1px solid #f0e7df" }} />
          </>
        )}

        {/* === Suite "choisir un profil" classique === */}
        {!hasAnnonce(annonce) && !isRefused && (
          <div>
            <div className="muted">Aucune annonce trouvée pour ce compte.</div>
            <button className="btn btn-primary" onClick={goCreateAnnonce}>Créer une annonce</button>
          </div>
        )}

        {hasAnnonce(annonce) && (
          <>
            <div className="status-row">
              <span className="status-label">Statut annonce :</span>
              <Badge statut={annonce.statut || "en_attente"} />
            </div>

            {annonce.statut !== "publiee" && !isRefused && (
              <div className="alert alert--info">
                ℹ️ Votre annonce n’est pas encore en ligne. Vous pouvez quand même choisir un profil pour continuer dans l’app.
              </div>
            )}

            <div className="search-row">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher par nom ou profession…"
                className="search"
              />
              <button onClick={loadAll} className="btn btn-ghost">Rafraîchir</button>
            </div>

            {filtered.length === 0 ? (
              <div className="muted">Aucun profil trouvé.</div>
            ) : (
              <div className="grid">
                {filtered.map((p) => (
                  <button
                    key={p._id || p.name}
                    className="profil-card"
                    onClick={() => selectProfil(p)}
                  >
                    <div className="profil-name">{p.name || "Profil"}</div>
                    <div className="profil-sub">{p.profession || "—"}</div>
                    {Array.isArray(p.activities) && p.activities.length > 0 ? (
                      <div className="profil-activities">
                        {p.activities.slice(0, 3).map((a, i) => (
                          <span key={i} className="pill">{a.name}</span>
                        ))}
                        {p.activities.length > 3 && (
                          <span className="pill pill--more">+{p.activities.length - 3}</span>
                        )}
                      </div>
                    ) : (
                      <div className="muted muted--small">Aucune activité</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="actions">
              <button className="btn btn-secondary" onClick={goManageAnnonce}>Gérer mes profils</button>
              <button className="btn btn-ghost" onClick={() => navigate(-1)}>Retour</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Badge({ statut }) {
  const map = {
    valide:     { cls: "badge badge--valide",   label: "validée" },
    refuse:     { cls: "badge badge--refuse",   label: "refusée" },
    publiee:    { cls: "badge badge--publiee",  label: "publiée" },
    en_attente: { cls: "badge badge--attente",  label: "en attente" },
  };
  const key  = ["valide", "refuse", "publiee", "en_attente"].includes(statut) ? statut : "en_attente";
  const conf = map[key];
  return <span className={conf.cls}>{conf.label}</span>;
}
